import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Inbox, Users, LogOut, Menu, Database, Trophy, Video,
  Flag,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { BrandLogo } from '@/components/brand/BrandLogo';
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from '@/components/ui/sheet';

export function MobileNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();

  const displayName = user?.name ?? 'Admin';

  /**
   * Same predicate as RootPage and PublicNav.
   *
   * This drawer is admin-console chrome, but `ProtectedRoute` gates /admin on
   * being SIGNED IN, not on being an admin — so a sailor who follows a stale
   * link renders this whole menu and reads off the shape of the console. The
   * API refuses them at every one of these routes, so nothing leaks but the
   * map; a menu of doors that all say no is still the wrong thing to show.
   */
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const navItems = isAdmin ? [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
    { label: 'Inbox', icon: Inbox, path: '/admin/inbox' },
    // #1478: user-submitted races. Next to Inbox because it is the same kind of
    // work — something arrived, somebody has to build it.
    { label: 'Pending races', icon: Flag, path: '/admin/pending-races' },
    { label: 'Video Locker', icon: Video, path: '/admin/video-locker' },
    { label: 'Ingestion', icon: Database, path: '/admin/ingestion' },
    { label: 'Standings', icon: Trophy, path: '/admin/standings-import' },
    { label: 'Users', icon: Users, path: '/admin/users' },
  ] : [];

  const handleNav = (path: string) => {
    navigate(path);
  };

  return (
    <Sheet>
      <SheetTrigger
        className="flex h-10 w-10 items-center justify-center rounded-md text-white hover:bg-white/10 transition-colors md:hidden"
        aria-label="Open navigation menu"
      >
        <Menu className="h-5 w-5" />
      </SheetTrigger>
      <SheetContent side="left" className="w-72 bg-navy-900 p-0 border-none">
        <SheetHeader className="border-b border-white/10 px-5 py-4">
          <SheetTitle>
            <BrandLogo variant="dark" />
          </SheetTitle>
          <SheetDescription className="text-sm text-white/60">
            {displayName}
          </SheetDescription>
        </SheetHeader>
        <nav className="flex flex-col gap-1 p-3">
          {navItems.map((item) => {
            const isActive =
              item.path === '/admin'
                ? location.pathname === '/admin'
                : location.pathname.startsWith(item.path);
            return (
              <SheetClose asChild key={item.path}>
                <button
                  onClick={() => handleNav(item.path)}
                  className={`flex items-center gap-3 rounded-lg px-3 min-h-[44px] text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-white/15 text-white'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </button>
              </SheetClose>
            );
          })}
        </nav>
        <div className="mt-auto border-t border-white/10 p-3">
          <SheetClose asChild>
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="flex w-full items-center gap-3 rounded-lg px-3 min-h-[44px] text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors"
            >
              <LogOut className="h-5 w-5" />
              Sign Out
            </button>
          </SheetClose>
        </div>
      </SheetContent>
    </Sheet>
  );
}
